import {LogoutForm} from "components/src/auth/LogoutForm";
import {useAuthenticatedUserContext} from "components/src/hook/UseAuthenticatedUserContext";
import {useSessionTimeout} from "components/src/hook/UseSessionTimeout";
import "./AppLayout.scss";
import {useTranslationWithOverride} from "components/src/hook/UseTranslationWithOverride";
import {SidebarCollapseProvider, SidebarToggleButton, useSidebarCollapsed,} from "components/src/sidebar/SideBar";
import {User as AdminIcon} from "iconoir-react";
import {ComponentType, RefObject, useEffect, useRef, useState} from "react";
import Nav from "react-bootstrap/Nav";
import NavDropdown from "react-bootstrap/NavDropdown";
import Row from "react-bootstrap/Row";
import {Outlet} from "react-router-dom";
import "components/src/styles/Styles.scss";

/** Default inactivity timeout: 30 minutes, matching the default Spring session timeout. */
const DEFAULT_SESSION_TIMEOUT_MS = 30 * 60 * 1000;

interface AdminLayoutProps {
    AppSidebar: ComponentType<{
        className?: string;
    }>;
    Menu?: ComponentType<{
        className?: string;
    }>;
    OptionalSidePanel?: ComponentType<{
        className?: string;
    }>;
    /**
     * Async function that resolves to the session inactivity timeout in milliseconds.
     * Called once on mount; if it rejects, the default of {@link DEFAULT_SESSION_TIMEOUT_MS}
     * is used. The returned value must match the backend's configured session timeout so
     * the frontend and backend expire at the same time.
     */
    fetchSessionTimeout: () => Promise<number>;
    /**
     * Lightweight authenticated backend call invoked (throttled) whenever user activity
     * is detected, keeping the backend session timer in sync with the frontend timer.
     * Any errors must be swallowed by the implementation; session expiry is handled
     * separately via 401 responses on regular API calls.
     */
    keepSessionAlive: () => void;
}

interface MenuDropdownProps {
    Menu?: ComponentType<{
        className?: string;
    }>;
    logoutSubmitButtonRef: RefObject<HTMLButtonElement>;
}

function SidebarLogo() {
    const collapsed = useSidebarCollapsed();
    const { t } = useTranslationWithOverride();

    return collapsed ? (
        <img
            src={t(["layout.logo-icon", "layout.logo"])}
            height="32"
            alt="CipherMail Logo"
        />
    ) : (
        <img src={t(["layout.logo"])} width="132" alt="CipherMail Logo" />
    );
}

function userInitials(username: string): string {
    return username
        .split(/[\s._@-]+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part[0].toUpperCase())
        .join("");
}

function MenuDropdown({
    Menu,
    logoutSubmitButtonRef,
}: Readonly<MenuDropdownProps>) {
    const { authenticatedUser, clearAuthenticatedUser } =
        useAuthenticatedUserContext();
    /**
     * i18next translation hook
     */
    const { t } = useTranslationWithOverride();

    const initials = authenticatedUser ? (
        userInitials(authenticatedUser)
    ) : (
        <AdminIcon />
    );

    const avatarTitle = (
        <span className="sidebar-user-avatar-trigger d-flex align-items-center gap-2">
            <span className="sidebar-user-avatar">{initials}</span>
            <span className="sidebar-user-name">{authenticatedUser}</span>
        </span>
    );

    return (
        <NavDropdown
            title={avatarTitle}
            className="sidebar-user-dropdown"
            drop="up">
            {Menu !== undefined && <Menu />}
            <NavDropdown.Item
                onClick={() => {
                    clearAuthenticatedUser();
                    logoutSubmitButtonRef.current?.click();
                }}>
                {t("login.logout")}
            </NavDropdown.Item>
        </NavDropdown>
    );
}

/**
 * Represents the main layout of the authenticated page.
 */
export const AppLayout = ({
    AppSidebar,
    Menu,
    OptionalSidePanel,
    fetchSessionTimeout,
    keepSessionAlive,
}: AdminLayoutProps) => {
    const { clearAuthenticatedUser } = useAuthenticatedUserContext();
    /**
     * A reference to the invisible logout submit button which can be used to programmatically submit
     * the logout form when the user selects logout from the menu
     */
    const logoutSubmitButtonRef = useRef<HTMLButtonElement>(null);

    const [sessionTimeoutMs, setSessionTimeoutMs] = useState<number>(
        DEFAULT_SESSION_TIMEOUT_MS,
    );

    // Store in a ref so the effect does not need fetchSessionTimeout as a dependency,
    // avoiding repeated fetches if the prop identity changes between renders.
    const fetchSessionTimeoutRef = useRef(fetchSessionTimeout);
    fetchSessionTimeoutRef.current = fetchSessionTimeout;

    useEffect(() => {
        fetchSessionTimeoutRef
            .current()
            .then((ms) => setSessionTimeoutMs(ms))
            .catch(() => setSessionTimeoutMs(DEFAULT_SESSION_TIMEOUT_MS));
    }, []);

    useSessionTimeout(
        sessionTimeoutMs,
        () => {
            clearAuthenticatedUser();
            logoutSubmitButtonRef.current?.click();
        },
        keepSessionAlive,
    );
    return (
        <div className="container-fluid d-flex flex-column vh-100">
            <SidebarCollapseProvider>
                <Row className="flex-grow-1 min-height-0 flex-nowrap">
                    <div className="sidebar sidebar-column d-flex flex-column h-100 p-0">
                        <div className="navbar-brand sidebar-header">
                            <div className="d-flex align-items-center gap-2">
                                <SidebarLogo />
                            </div>
                            <SidebarToggleButton />
                        </div>

                        <div className="sidebar-content flex-fill overflow-y-auto">
                            {<AppSidebar />}
                        </div>

                        {OptionalSidePanel !== undefined && (
                            <OptionalSidePanel />
                        )}

                        <div className="sidebar-user-bar">
                            <div className="d-flex align-items-center justify-content-between">
                                <Nav className="z-index-popover flex-grow-1">
                                    <MenuDropdown
                                        Menu={Menu}
                                        logoutSubmitButtonRef={
                                            logoutSubmitButtonRef
                                        }
                                    />
                                </Nav>
                            </div>
                        </div>
                    </div>

                    <main className="d-flex flex-fill flex-column h-100 overflow-x-hidden overflow-y-auto">
                        <div className="main-content-wrapper d-flex flex-column flex-fill bg-body">
                            {/* Portal target for page-level notifications (e.g. CheckLicense) !! DO NOT REMOVE !! */}
                            <div id="header" />
                            {/* The child "pages" will be added here*/}
                            <Outlet />
                        </div>
                    </main>
                </Row>
            </SidebarCollapseProvider>

            <LogoutForm ref={logoutSubmitButtonRef} />
        </div>
    );
};
